import { Button, Grid, Paper, Typography } from '@mui/material'
import Image from '@components/Image'
import { BsQuestionSquare } from 'react-icons/bs'
import { Control, useFormContext } from 'react-hook-form'
import { colorSelection, backgroundColorSelection } from '@utils/colorSelection'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ModuleAccessConfigDialog } from '@components/ModuleAccessConfigDialog'
import useAPIFetch from '@hooks/useAPIFetch'
import ModuleService from '@services/module.service'
import { Module } from '@models/module.model'
import { RoleInfo } from '@models/role.model'

export const PermissionSettingTable = (props: {
	control: Control<RoleInfo, any>
	permissionSettingItems: any
}) => {
	const { setValue, getValues } = useFormContext()
	const getIconColor = (isSubscribe: boolean, code: string) => {
		if (!isSubscribe) {
			return undefined
		} else {
			return colorSelection(code)
		}
	}

	const getBackgroundColor = (isSubscribe: boolean, code: string) => {
		if (!isSubscribe) {
			return 'info'
		} else {
			return backgroundColorSelection(code)
		}
	}

	const { t } = useTranslation()
	const { setRequest, isLoading } = useAPIFetch()
	const [open, setOpen] = useState(false)
	const [moduleList, setModuleList] = useState<Module[]>([])
	const selectedProductModuleRef = useRef<{
		itemType: string
		itemId: string
	}>()

	useEffect(() => {
		if (!open) {
			setModuleList([])
			selectedProductModuleRef.current = undefined
		} else {
			setRequest({
				callback: async (cancelToken: any) => {
					if (selectedProductModuleRef.current) {
						const resp = await ModuleService.GetModuleList({
							product_id:
								selectedProductModuleRef.current.itemType === 'product'
									? selectedProductModuleRef.current.itemId
									: undefined,
							moduleId:
								selectedProductModuleRef.current.itemType === 'module'
									? selectedProductModuleRef.current.itemId
									: undefined,
						})

						setModuleList(resp.data)
						setOpen(true)
					}
				},
			})
		}
	}, [open])

	return (
		<Paper
			sx={{
				display: 'flex',
				alignItems: 'center',
				padding: '30px',
				minHeight: '120px',
			}}
		>
			<Grid container columnSpacing={2} rowSpacing={2}>
				{props.permissionSettingItems?.map(
					(
						elem: {
							id?: string
							purchase_order_id?: string
							project_id?: string
							item_id?: string
							item_type?: string
							icon_url?: string
							code?: string
							display_name?: string
							isSubscribe: boolean
						},
						index: number,
					) => {
						return (
							<Grid item key={`psm_${elem.id}`}>
								<Paper elevation={3} sx={{ borderRadius: '10px' }}>
									<div
										style={{
											display: 'flex',
											flexDirection: 'column',
										}}
									>
										<Button
											sx={{
												width: '200px',
												height: '100px',
												color: '#707070',
												':hover': {
													backgroundColor: getBackgroundColor(elem.isSubscribe, elem.code ?? ''),
												},
												backgroundColor: elem.isSubscribe
													? getBackgroundColor(elem.isSubscribe, elem.code ?? '')
													: 'white',
											}}
											onClick={() => {
												setValue(
													`project_product_subs.${index}.isSubscribe`,
													!getValues(`project_product_subs.${index}.isSubscribe`),
													{ shouldDirty: true },
												)
											}}
										>
											{elem.icon_url ? (
												<Image
													src={elem.icon_url}
													width={50}
													height={50}
													themeColor={getIconColor(elem.isSubscribe, elem.code ?? '') || undefined}
													sx={{ width: '25%' }}
												/>
											) : (
												<BsQuestionSquare
													style={{
														fontSize: '50px',
														color: elem.isSubscribe ? 'green' : '#707070',
													}}
												/>
											)}
											<Typography variant="h6" sx={{ width: '60%' }}>
												{elem.display_name}
											</Typography>
										</Button>

										{getValues(`project_product_subs.${index}.isSubscribe`) && (
											<Button
												style={{ color: 'black' }}
												variant="outlined"
												onClick={() => {
													if (elem.item_type && elem.item_id) {
														selectedProductModuleRef.current = {
															itemType: elem.item_type,
															itemId: elem.item_id,
														}
														setOpen(true)
													}
												}}
											>
												{t('Permissions')}
											</Button>
										)}
									</div>
								</Paper>
							</Grid>
						)
					},
				)}
			</Grid>

			<ModuleAccessConfigDialog
				control={props.control}
				moduleList={moduleList}
				opened={open}
				close={() => {
					setOpen(false)
				}}
			/>
		</Paper>
	)
}
